import { useMutation } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import RedeemCodeDateField from '../../RedeemCodes/components/RedeemCodeDateField';
import { CREATE_DEEP_LINK, DELETE_DEEP_LINK, UPDATE_DEEP_LINK } from '../graphql';
import {
  DeepLinkItem,
  DeepLinkTarget,
  FALLBACK_TARGETS,
  formatDeepLinkError,
  REDEEM_CODE_REGEX,
  TARGET_INFO,
} from '../utils';

type Props = {
  // null = create mode, otherwise the deep link being edited
  deepLink: DeepLinkItem | null;
  onSaved: (deepLink: DeepLinkItem) => void;
  onCancel?: () => void;
  onDeleted?: (id: string) => void;
};

const defaultEndDate = () => moment().utc().set({ second: 0, millisecond: 0 }).add(30, 'days').toDate();

const getParamValue = (deepLink: DeepLinkItem | null): string => {
  const paramKey = deepLink ? TARGET_INFO[deepLink.target]?.param?.key : undefined;
  return (paramKey && deepLink?.params?.[paramKey]) || '';
};

const DeepLinkForm = ({ deepLink, onSaved, onCancel, onDeleted }: Props) => {
  const isCreateMode = deepLink === null;

  const [name, setName] = useState(deepLink?.name ?? '');
  const [campaign, setCampaign] = useState(deepLink?.campaign ?? '');
  const [target, setTarget] = useState<DeepLinkTarget>(deepLink?.target ?? DeepLinkTarget.SHOP);
  const [paramValue, setParamValue] = useState(getParamValue(deepLink));
  const [fallbackTarget, setFallbackTarget] = useState<string>(deepLink?.fallbackTarget ?? '');
  const [enabled, setEnabled] = useState(deepLink?.enabled ?? true);
  const [hasEndDate, setHasEndDate] = useState(!_.isNil(deepLink?.endDate));
  const [endDate, setEndDate] = useState<Date>(deepLink?.endDate ? new Date(deepLink.endDate) : defaultEndDate());
  const [maxUsesText, setMaxUsesText] = useState(deepLink?.maxUses ? String(deepLink.maxUses) : '');
  const [saveError, setSaveError] = useState<string | null>(null);

  const [createDeepLink, { loading: createLoading }] = useMutation(CREATE_DEEP_LINK);
  const [updateDeepLink, { loading: updateLoading }] = useMutation(UPDATE_DEEP_LINK);
  const [deleteDeepLink, { loading: deleteLoading }] = useMutation(DELETE_DEEP_LINK);

  const loading = createLoading || updateLoading || deleteLoading;

  // Reset the form whenever another deep link is selected or create mode is entered
  useEffect(() => {
    setName(deepLink?.name ?? '');
    setCampaign(deepLink?.campaign ?? '');
    setTarget(deepLink?.target ?? DeepLinkTarget.SHOP);
    setParamValue(getParamValue(deepLink));
    setFallbackTarget(deepLink?.fallbackTarget ?? '');
    setEnabled(deepLink?.enabled ?? true);
    setHasEndDate(!_.isNil(deepLink?.endDate));
    setEndDate(deepLink?.endDate ? new Date(deepLink.endDate) : defaultEndDate());
    setMaxUsesText(deepLink?.maxUses ? String(deepLink.maxUses) : '');
    setSaveError(null);
  }, [deepLink]);

  const param = TARGET_INFO[target].param;
  const trimmedParamValue = param?.key === 'redeemCode' ? paramValue.trim().toUpperCase() : paramValue.trim();
  const paramIsValid =
    !param ||
    ((param.required ? trimmedParamValue.length > 0 : true) &&
      (param.key !== 'redeemCode' || !trimmedParamValue || REDEEM_CODE_REGEX.test(trimmedParamValue)));
  const maxUses = maxUsesText.trim() ? Number(maxUsesText) : null;
  const maxUsesIsValid = maxUses === null || (Number.isInteger(maxUses) && maxUses >= 1);
  const canSave = name.trim().length > 0 && paramIsValid && maxUsesIsValid && !loading;

  const handleTargetChange = (nextTarget: DeepLinkTarget) => {
    // Values are not shared between targets (a box ID is not a content pack ID)
    if (TARGET_INFO[nextTarget].param?.key !== param?.key) {
      setParamValue('');
    }
    setTarget(nextTarget);
  };

  const handleSave = () => {
    setSaveError(null);
    const variables = {
      name: name.trim(),
      campaign: campaign.trim() ? campaign.trim() : null,
      target,
      params: param && trimmedParamValue ? { [param.key]: trimmedParamValue } : null,
      fallbackTarget: fallbackTarget || null,
      enabled,
      endDate: hasEndDate ? endDate : null,
      maxUses,
    };

    const request = isCreateMode
      ? createDeepLink({ variables }).then(({ data }) => data?.createDeepLink)
      : updateDeepLink({ variables: { ...variables, id: deepLink.id } }).then(({ data }) => data?.updateDeepLink);

    request
      .then((saved) => {
        if (saved) {
          onSaved(saved as DeepLinkItem);
        }
      })
      .catch((error) => {
        console.error(error);
        setSaveError(formatDeepLinkError(error));
      });
  };

  const handleDelete = () => {
    if (isCreateMode || !onDeleted) {
      return;
    }
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Delete "${deepLink.name}"? The link will stop working and its open stats are lost. ` +
          'To stop a link that was already shared, turn it off instead.'
      )
    ) {
      return;
    }
    deleteDeepLink({ variables: { id: deepLink.id } })
      .then(() => {
        onDeleted(deepLink.id);
      })
      .catch((error) => {
        console.error(error);
        setSaveError(formatDeepLinkError(error));
      });
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Summer sale – Instagram"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Form.Text className="text-muted">Only shown in the admin.</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Campaign (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. summer_sale_2026"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
            />
            <Form.Text className="text-muted">Tag sent to analytics when a player opens the link.</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Screen to open</Form.Label>
            <Form.Control
              as="select"
              value={target}
              onChange={(e) => handleTargetChange(e.target.value as DeepLinkTarget)}
            >
              {Object.values(DeepLinkTarget).map((value) => (
                <option key={value} value={value}>
                  {TARGET_INFO[value].label}
                </option>
              ))}
            </Form.Control>
            <Form.Text className="text-muted">
              If this screen is not available for the player (no running event, feature still locked), the link does
              nothing and the game opens as usual.
            </Form.Text>
          </Form.Group>

          {param && (
            <Form.Group>
              <Form.Label>{param.label}</Form.Label>
              <Form.Control
                type="text"
                placeholder={param.placeholder}
                value={paramValue}
                isInvalid={!paramIsValid && paramValue.length > 0}
                onChange={(e) => setParamValue(e.target.value)}
              />
              <Form.Text className="text-muted">{param.help}</Form.Text>
            </Form.Group>
          )}
        </Col>

        <Col md={6}>
          <fieldset className="border rounded p-3 mb-3">
            <legend className="w-auto px-2 h6 mb-0">Availability</legend>

            <Form.Check
              type="switch"
              id="deep-link-enabled"
              className="mb-3"
              label={<span className="font-weight-bold">Enabled</span>}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />

            <Form.Check
              type="checkbox"
              id="deep-link-has-end-date"
              label="Expires"
              checked={hasEndDate}
              onChange={(e) => setHasEndDate(e.target.checked)}
            />
            {hasEndDate && <RedeemCodeDateField label="Expiry date" value={endDate} onChange={setEndDate} />}

            <Form.Group className="mt-3">
              <Form.Label>Max players (optional)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="Leave empty for unlimited"
                value={maxUsesText}
                isInvalid={!maxUsesIsValid}
                onChange={(e) => setMaxUsesText(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">Must be a whole number of at least 1.</Form.Control.Feedback>
              <Form.Text className="text-muted">
                Counts different players. Players who already opened the link can keep using it.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>When disabled, expired or full, open instead</Form.Label>
              <Form.Control as="select" value={fallbackTarget} onChange={(e) => setFallbackTarget(e.target.value)}>
                <option value="">Nothing, the game opens as usual (default)</option>
                {FALLBACK_TARGETS.filter((value) => value !== DeepLinkTarget.HOME).map((value) => (
                  <option key={value} value={value}>
                    {TARGET_INFO[value].label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </fieldset>
        </Col>
      </Row>

      {saveError && <Alert variant="danger">{saveError}</Alert>}

      <Row className="mt-3">
        <Col className="d-flex align-items-center justify-content-end">
          {!isCreateMode && onDeleted && (
            <Button variant="outline-danger" className="mr-auto" disabled={loading} onClick={handleDelete}>
              <i className="fas fa-trash-alt mr-2" />
              Delete
            </Button>
          )}
          {isCreateMode && onCancel && (
            <Button variant="outline-secondary" className="mr-2" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant={isCreateMode ? 'success' : 'primary'} disabled={!canSave} onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : isCreateMode ? 'Generate link' : 'Save'}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default DeepLinkForm;
